import { Injectable, OnDestroy } from "@angular/core";
import { LocationDataModel, LocationDetailViewModel, LocationOpenDayViewModel, LocationOpenSpecialDayViewModel, LocationOpenTime, OrdertypeViewModel } from "../../shared/models";
import { Subscription, Subject, Observable } from "rxjs";
import { LikeService } from "../../services/like.service";
import { CoreLogService } from "../../services/log.service";
import { ContentType, ImageOrientation, CustomAction, orderTypes } from "../../shared/contentTypes";
import * as moment from "moment";
import Utils from "../../shared/utils";
import { CircularInjectionHelper } from "../../services/circularInjectionHelper";
import { IObjectObserver } from "../../services/IObjectObserver";
import { ObjectObserverFactory } from "../../services/objectObserverFactory";
import { ReservationSettingService } from "../../services/reservationSetting.service";
import { Coordinates } from "../../services/com/comEnvironmentGeolocationCoordinates";
import { CoreCOMService } from "../../services/com.service";
import { CoreOpenDayTime } from "../../languages/openDayTime";
import { map } from "rxjs/internal/operators/map";
import { WebshopOrdertypesService } from "../../services/webShopOrdertypes.service";
import { DeviceLocationService } from "../../services/deviceLocation.service";

@Injectable()
export class LocationDetailService implements OnDestroy {
  private _locationObsServ: IObjectObserver<LocationDataModel>;
  private _locationSupscription: Subscription;
  /** Subject to easy observe changes of location */
  private _dataChange: Subject<(string | number)[]>;

  constructor(private _likeService: LikeService,
    private _coreComService: CoreCOMService,
    private _reservationSettingService: ReservationSettingService,
    private _coreLogService: CoreLogService,
    private _webshopOrdertypesService: WebshopOrdertypesService,
    private _deviceLocationService: DeviceLocationService,
    private _objObsInjectHelper: CircularInjectionHelper) {
    this._locationObsServ = ObjectObserverFactory.GetObjectObserverByInjectionHelper<LocationDataModel>(ContentType.Location, _objObsInjectHelper);

    this._dataChange = new Subject<(string | number)[]>();

    this._locationSupscription = this._locationObsServ.ObserveChanges.subscribe((keys) => {
      this._dataChange.next(keys);
    });

  }

  public get ObserveChanges(): Observable<(string | number)[]> {
    return this._dataChange.asObservable();
  }

  public getSupportServices$(hyp_cus_nr: number): Observable<OrdertypeViewModel[]> {
    return this._webshopOrdertypesService.supportedOrdertypes$.pipe(
      map(ordertypes => ordertypes.filter(ordertype => ordertype.supported && ordertype.show_in_location && ordertype.locationIDs.includes(hyp_cus_nr))),
    );
  }

  /**
  * Getting the list of locationDataModel
  * Create locationViewModelList
  * @result return a promise that resolves a list of locationViewModel
  */
  public GetLocationDetailViewModel(id: number | string, hasClockLabel: boolean = false): Promise<LocationDetailViewModel> {
    const me = this;
    return new Promise<LocationDetailViewModel>((resolve, reject) => {
      // gets undeleted locations
      me._locationObsServ.GetObject(id)
        .then(async (location) => {
          let coordinates: Coordinates = null;
          if (me._coreComService.environment.client.isMobile) {
            coordinates = await me._deviceLocationService.getCurrentPosition();
          }
          // TODO muss das Datum und Zeitzone des Standorts betrachtet werden?
          const now = moment();
          const currentTimeYMD = Utils.GetYMDFromMoment(now);
          const currentTimeHMS = Utils.GetHMSFromMoment(now);
          me.mapDataModelToDetailViewModel(location, currentTimeYMD, currentTimeHMS, coordinates, hasClockLabel)
            .then(resolve)
            .catch(e => {
              me._objObsInjectHelper.coreLogService.error("LocationDetailService.GetLocationViewModel", "mapDataModelToDetailViewModel", e);
              reject();
            });
        })
        .catch(e => {
          me._objObsInjectHelper.coreLogService.error("LocationDetailService.GetLocationViewModel", "GetObject", e);
          reject();
        });
    });
  }

  // Mapper

  /**
   * map a locationDataModel to locationViewModel
   * @param model Get a locationDataModel for mapping
   * @param currentTimeYMD used for setting openTime
   * @param currentTimeHMS used for setting openTime
   * @result return a locationViewModel
   */
  private async mapDataModelToDetailViewModel(model: LocationDataModel, currentTimeYMD: number, currentTimeHMS, coordinates: Coordinates, hasClockLabel: boolean = false): Promise<LocationDetailViewModel> {
    const me = this;
    if (model) {
      const viewModel = new LocationDetailViewModel();
      viewModel.model = model;
      viewModel.hyp_cus_nr = model.hyp_cus_nr;
      viewModel.label_de = model.label_de;
      viewModel.label_en = model.label_en;
      viewModel.street = model.street;
      viewModel.zip = model.zip;
      viewModel.location = model.location;
      viewModel.country_id = model.country_id;
      viewModel.phone_no = model.phone_no;
      viewModel.mail = model.mail;
      viewModel.homepage = model.homepage;
      viewModel.latitude = model.latitude;
      viewModel.longitude = model.longitude;
      viewModel.sort_nr = model.sort_nr;
      viewModel.impressum_html_de = model.impressum_html_de;
      viewModel.impressum_html_en = model.impressum_html_en;
      viewModel.stnr = model.stnr;
      viewModel.ustid = model.ustid;
      viewModel.commercial_register = model.commercial_register;
      viewModel.local_court = model.local_court;
      viewModel.representative = model.representative;
      viewModel.image_photo = ContentType.Location + "/" + model.hyp_cus_nr + "_photo_" + ImageOrientation.Landscape + "/" + model.last_image_update_at_ymd + "/" + model.last_image_update_at_hms;
      viewModel.image_map = model.last_map_image_update_by === 0 ? "" : ContentType.Location + "/" + model.hyp_cus_nr + "_map_" + ImageOrientation.Landscape + "/" + model.last_map_image_update_at_ymd + "/" + model.last_map_image_update_at_hms;
      viewModel.distance = coordinates && model.longitude > 0 && model.latitude > 0 ? +(Utils.getDistanceBetweenPoints(coordinates.latitude, coordinates.longitude, model.latitude, model.longitude).toFixed(1)) : 1000000;
      viewModel.open_days = model.open_days;

      const markYMD = model.GetTheoreticalOpenYMD(currentTimeYMD, currentTimeHMS);
      const markMoment = Utils.GetMomentFromInt(markYMD);
      const markWeekday = markMoment.isoWeekday();
      const next7days = new Array(7).fill(0).map((_, i) => markMoment.clone().add(i, "day"));
      viewModel.openDays = next7days.map(day => new LocationOpenDayViewModel(model, day, orderTypes.allUnknown));
      viewModel.openDaysDelivery = next7days.map(day => new LocationOpenDayViewModel(model, day, orderTypes.delivery));
      viewModel.openDaysPickup = next7days.map(day => new LocationOpenDayViewModel(model, day, orderTypes.pickup));
      viewModel.openDaysInHause = next7days.map(day => new LocationOpenDayViewModel(model, day, orderTypes.inHouse));
      viewModel.openDaysOrderahead = next7days.map(day => new LocationOpenDayViewModel(model, day, orderTypes.orderAhead));
      const specialDaysUpToYMD = Utils.GetYMDFromMoment(markMoment.clone().add(30, "day"));
      const nextMonthSpecialDays = model.open_specialdays.filter(day => day.specialday <= specialDaysUpToYMD && day.specialdayTo >= markYMD);
      viewModel.upcomingSpecialDays = nextMonthSpecialDays
        .filter(day => day.type === orderTypes.allUnknown)
        .map(day => new LocationOpenSpecialDayViewModel(model, day, markYMD));
      try {
        viewModel.like = await me._likeService.HasLike(ContentType.Location, model.hyp_cus_nr);
      } catch (error) {
        me._coreLogService.error("LocationDetailService.mapDataModelToDetailViewModel", "HasLike try catch", error);
      }
      viewModel.locationActions = me.setLocationActionList(model);
      try {
        const reservationSetting = await me._reservationSettingService.GetReservatonSetting();
        viewModel.hasReservation = reservationSetting && reservationSetting.includes(model.hyp_cus_nr);
        if (viewModel.hasReservation) {
          viewModel.locationActions.push(CustomAction.reservationAction);
        }
      } catch (error) {
        me._coreLogService.error("LocationDetailService.mapDataModelToDetailViewModel", "GetReservatonSetting try catch", error);
      }
      return Promise.resolve(viewModel);
    } else {
      return Promise.resolve(null);
    }
  }

  private setLocationActionList(locationDetail: LocationDataModel): CustomAction[] {
    const actionList: CustomAction[] = [];
    if (locationDetail.phone_no !== "") {
      actionList.push(CustomAction.callAction);
    }
    if (!Utils.IsNull(locationDetail.latitude) && locationDetail.longitude) {
      actionList.push(CustomAction.directionAction);
    }
    if (locationDetail.mail !== "") {
      actionList.push(CustomAction.emailAction);
    }
    return actionList;
  }

  public ngOnDestroy(): void {
    const me = this;
    if (me._locationSupscription) {
      me._locationSupscription.unsubscribe();
    }
  }

}
